import { Alert, Box, CircularProgress, createTheme, CssBaseline, ThemeProvider, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { googleAuthService } from "../services/googleAuth";
import { MessageService } from "../services/MessageService";

// Use the same theme as ChatInterface
const theme = createTheme({
  palette: {
    primary: {
      main: "#2c3e50",
      light: "#34495e",
      dark: "#1a252f",
    },
    secondary: {
      main: "#95a5a6",
      light: "#bdc3c7",
      dark: "#7f8c8d",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2c3e50",
      secondary: "#7f8c8d",
    },
    divider: "#ecf0f1",
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
});

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("Processing authentication...");

  useEffect(() => {
    let isMounted = true;
    let redirectTimeout: NodeJS.Timeout | null = null;

    const handleCallback = async () => {
      try {
        if (!isMounted) return;
        setStatus("Exchanging authorization code...");

        const user = await googleAuthService.handleCallback();

        if (!isMounted) return;

        if (user) {
          setStatus("Loading your conversations...");
          // Load user threads to ensure everything is ready
          try {
            await MessageService.getUserThreads();
            if (!isMounted) return;
            setStatus("Authentication successful! Redirecting...");
            // Small delay to show success message
            redirectTimeout = setTimeout(() => {
              if (isMounted) {
                navigate("/", { replace: true });
              }
            }, 1000);
          } catch (error) {
            console.error("Error loading user threads:", error);
            if (!isMounted) return;
            // Still redirect even if threads fail to load
            setStatus("Authentication successful! Redirecting...");
            redirectTimeout = setTimeout(() => {
              if (isMounted) {
                navigate("/", { replace: true });
              }
            }, 1000);
          }
        } else {
          if (!isMounted) return;
          // Check if user is already authenticated (code already processed)
          const currentUser = googleAuthService.getCurrentUser();
          if (currentUser) {
            setStatus("Already authenticated! Redirecting...");
            redirectTimeout = setTimeout(() => {
              if (isMounted) {
                navigate("/", { replace: true });
              }
            }, 1000);
          } else {
            setStatus("Authentication completed! Redirecting...");
            redirectTimeout = setTimeout(() => {
              if (isMounted) {
                navigate("/", { replace: true });
              }
            }, 1000);
          }
        }
      } catch (error) {
        console.error("Error handling OAuth callback:", error);
        if (isMounted) {
          setError("Authentication failed. Please try again.");
        }
      }
    };

    handleCallback();

    return () => {
      isMounted = false;
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [navigate]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
        }}
      >
        <CircularProgress size={80} />

        <Box sx={{ textAlign: "center", maxWidth: 400 }}>
          <Typography variant="h4" color="text.primary" gutterBottom>
            Signing you in...
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {status}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
